import { useMemo, useState } from 'react';

import { PatientFilter, PatientStatusFilter } from './ClinicPatientsFilter';

const defaultState: PatientFilter = {
  status: 'active',
  treatment: '',
  provider: '',
  view: 'my',
};

const ALLOWED_STATUSES: ReadonlySet<PatientStatusFilter> = new Set(['all', 'active', 'inactive']);

export function useClinicPatientsFilters(clinicId: string | undefined): {
  filters: PatientFilter;
  updateFilters: (filters: PatientFilter) => void;
} {
  const filtersKey = clinicId && `filters-clinic-patients-${clinicId}`;
  const initialState = useMemo<PatientFilter>(() => {
    if (!filtersKey) {
      return defaultState;
    }

    const state = sessionStorage.getItem(filtersKey);

    const parsedState: PatientFilter = {
      ...defaultState,
      ...(state ? JSON.parse(state) : {}),
    };

    // Self-heal stale status values from before the filter was simplified
    // (e.g. 'invited' or 'incomplete' lingering in sessionStorage).
    if (!ALLOWED_STATUSES.has(parsedState.status)) {
      parsedState.status = defaultState.status;
    }

    return parsedState;
  }, [filtersKey]);
  const [filters, setFilters] = useState<PatientFilter>(initialState);

  function updateFilters(filters: PatientFilter) {
    setFilters((prevFilters) => {
      const nextFilters = { ...prevFilters, ...filters };

      if (filtersKey) {
        sessionStorage.setItem(filtersKey, JSON.stringify(nextFilters));
      }

      return nextFilters;
    });
  }

  return {
    filters,
    updateFilters,
  };
}
